import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogContent, DialogActions,
    Button, Typography, Box, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, TablePagination, MenuItem, Select,
    FormControl, InputLabel, AppBar, Toolbar, IconButton, makeStyles, Chip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import LoaderWithBackDrop from '../../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp';
import { getInsiderDqtLedgerService, getInsiderDqtOrdersService } from '../../../../services/billing.service';

/**
 * DQInsiderDqtLedgerDialog — one workspace's DQ Token history.
 *
 * The DQT counterpart of the credit ledger dialog, opened from the insiders list.
 * Every row is an immutable entry: a grant against an offline payment, a checkout
 * purchase, or a conversion into credits or licences. Grants and purchases carry
 * the money and the rate behind them, so the balance traces back to what was paid.
 *
 * Read-only. DQT moves through the grant action, checkout, and the partner's own
 * conversions — never from here.
 */

const TRANSACTION_TYPES = {
    grant:      'Grant (offline payment)',
    purchase:   'Purchase (checkout)',
    conversion: 'Conversion',
    adjustment: 'Adjustment',
};

const TYPE_COLOR = {
    grant:      'primary',
    purchase:   'primary',
    conversion: 'default',
    adjustment: 'secondary',
};

const useStyles = makeStyles((theme) => ({
    dialogContent: { backgroundColor: '#f8f9fa', padding: theme.spacing(3) },
    table: { minWidth: 650 },
    filterContainer: {
        marginBottom: theme.spacing(2),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    balanceBox: {
        backgroundColor: '#0097b2',
        color: '#fff',
        padding: theme.spacing(1, 3),
        borderRadius: theme.spacing(1),
        display: 'inline-block',
        fontWeight: 'bold',
    },
    positiveAmount: { color: 'green', fontWeight: 'bold' },
    negativeAmount: { color: '#374151', fontWeight: 'bold' },
}));

const fmt = (n) => Number(n || 0).toLocaleString('en-US');

// One frozen object, so resetting the closed dialog to it changes no state.
const INITIAL_FILTERS = Object.freeze({ page: 1, limit: 20, transaction_type: '' });

const describeEntry = (e) => {
    if (e.transaction_type === 'grant') return `Granted against payment ${e.payment_reference || '—'}`;
    if (e.transaction_type === 'conversion') {
        const unit = e.conversion?.unit === 'licence' ? 'licences' : 'credits';
        return `Converted into ${fmt(e.conversion?.quantity)} ${unit}`;
    }
    return e.description || '—';
};

const DQInsiderDqtLedgerDialog = ({ open, onClose, dq_insider_id }) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [ledger, setLedger] = useState({ entries: [], total: 0, dqt_balance: 0 });
    const [orders, setOrders] = useState([]);
    const [filters, setFilters] = useState(INITIAL_FILTERS);

    // Depends on the filter VALUES, never on a function identity: this dialog is
    // mounted (closed) by the insiders list, and an object dependency here re-ran
    // the effect on every render.
    const { page, limit, transaction_type } = filters;
    useEffect(() => {
        if (!open || !dq_insider_id) return undefined;

        let cancelled = false;
        const fetchLedger = async () => {
            setLoading(true);
            try {
                const [res, ord] = await Promise.all([
                    getInsiderDqtLedgerService(dq_insider_id, page, limit, transaction_type || undefined),
                    // Orders give the money side its context: what was bought, and its invoice.
                    getInsiderDqtOrdersService(dq_insider_id, 1, 5),
                ]);
                if (cancelled) return;
                if (res && res.data) {
                    setLedger({
                        entries: res.data.entries || [],
                        total: res.data.total || 0,
                        dqt_balance: res.data.dqt_balance || 0,
                    });
                }
                setOrders(ord?.data?.orders || []);
            } catch (error) {
                // invokeApi has already shown the error.
                console.error('Error fetching DQT ledger:', error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchLedger();
        return () => { cancelled = true; };
    }, [open, dq_insider_id, page, limit, transaction_type]);

    // Clear on close only — `open` alone, so nothing here can feed the effect above.
    useEffect(() => {
        if (open) return;
        setLedger({ entries: [], total: 0, dqt_balance: 0 });
        setOrders([]);
        setFilters(INITIAL_FILTERS);
    }, [open]);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({
            ...prev,
            [field]: value,
            page: field === 'page' ? value : 1,
        }));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
            <AppBar position="static" color="white" elevation={0} style={{ borderBottom: '1px solid #eee' }}>
                <Toolbar style={{ justifyContent: 'space-between' }}>
                    <Typography variant="h6" color="primary">PBDQ DQ Token Ledger</Typography>
                    <IconButton edge="end" color="inherit" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <DialogContent className={classes.dialogContent}>
                <Box className={classes.filterContainer}>
                    <Box className={classes.balanceBox}>
                        <Typography variant="subtitle1">
                            Current Balance: {fmt(ledger.dqt_balance)} DQT
                        </Typography>
                    </Box>
                    <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
                        <InputLabel>Transaction Type</InputLabel>
                        <Select
                            value={filters.transaction_type}
                            onChange={(e) => handleFilterChange('transaction_type', e.target.value)}
                            label="Transaction Type"
                        >
                            <MenuItem value=""><em>All</em></MenuItem>
                            {Object.keys(TRANSACTION_TYPES).map((key) => (
                                <MenuItem key={key} value={key}>{TRANSACTION_TYPES[key]}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {orders.length > 0 && (
                    <Box mb={2}>
                        <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, letterSpacing: 0.5 }}>
                            RECENT ORDERS
                        </Typography>
                        {orders.map((o) => (
                            <Box key={o.order_id} display="flex" justifyContent="space-between" style={{ padding: '4px 0', borderTop: '1px solid #eef0f2', gap: 12 }}>
                                <Typography variant="body2" color="textSecondary" noWrap>
                                    {o.invoice_no || 'no invoice'} · {o.kind === 'offline_grant' ? 'grant' : o.kind} · {o.label}
                                    {' · '}{o.paid_display || o.usd_display}
                                </Typography>
                                <Typography variant="body2" style={{ fontWeight: 700, whiteSpace: 'nowrap', color: o.status === 'paid' ? '#16a34a' : '#6b7280' }}>
                                    {o.status} · {fmt(o.dqt_amount)} DQT
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                <TableContainer component={Paper} elevation={1}>
                    <Table className={classes.table}>
                        <TableHead>
                            <TableRow>
                                <TableCell><b>Date</b></TableCell>
                                <TableCell><b>Transaction Type</b></TableCell>
                                <TableCell><b>Detail</b></TableCell>
                                <TableCell align="right"><b>DQ Tokens</b></TableCell>
                                <TableCell align="right"><b>Balance After</b></TableCell>
                                <TableCell align="right"><b>Money (USD)</b></TableCell>
                                <TableCell align="right"><b>Rate</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {ledger.entries.map((entry) => (
                                <TableRow key={entry._id}>
                                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                                        {new Date(entry.createdAt).toLocaleString('en-GB', {
                                            day: '2-digit', month: 'short', year: 'numeric',
                                            hour: '2-digit', minute: '2-digit', hour12: true,
                                        })}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={TRANSACTION_TYPES[entry.transaction_type] || entry.transaction_type}
                                            size="small"
                                            color={TYPE_COLOR[entry.transaction_type] || 'default'}
                                            variant={entry.transaction_type === 'conversion' ? 'outlined' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell style={{ maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        <Typography variant="body2">{describeEntry(entry)}</Typography>
                                        {entry.created_by?.label ? (
                                            <Typography variant="caption" color="textSecondary">by {entry.created_by.label}</Typography>
                                        ) : null}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography className={entry.amount_dqt >= 0 ? classes.positiveAmount : classes.negativeAmount}>
                                            {entry.amount_dqt >= 0 ? '+' : '−'}{fmt(Math.abs(entry.amount_dqt))}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography style={{ fontWeight: 500 }}>{fmt(entry.balance_after)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {typeof entry.amount_usd === 'number'
                                            ? <Typography variant="body2">US${fmt(entry.amount_usd)}</Typography>
                                            : <Typography variant="body2" color="textSecondary">—</Typography>}
                                    </TableCell>
                                    <TableCell align="right">
                                        {typeof entry.usd_per_dqt === 'number'
                                            ? <Typography variant="body2" color="textSecondary">US${entry.usd_per_dqt}/DQT</Typography>
                                            : <Typography variant="body2" color="textSecondary">—</Typography>}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {ledger.entries.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">No DQ Token entries found.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 20, 50]}
                    component="div"
                    count={ledger.total}
                    rowsPerPage={filters.limit}
                    page={filters.page - 1}
                    onPageChange={(e, newPage) => handleFilterChange('page', newPage + 1)}
                    onRowsPerPageChange={(e) => handleFilterChange('limit', parseInt(e.target.value, 10))}
                />

                <LoaderWithBackDrop loading={loading} />
            </DialogContent>

            <DialogActions style={{ padding: '12px 24px' }}>
                <Button onClick={onClose} color="primary" variant="contained" disableElevation>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DQInsiderDqtLedgerDialog;
